/* eslint-disable max-lines */
import store from 'store';
import {
  OFFER_INIT_GUID,
  OFFER_LEGAL_CONDITIONS,
  OFFER_COMPONENTS_BY_PARTNER_ID,
  OFFER_DOCUMENTS_REQUIRED_BY_GUID,
  OFFER_SET_CURRENT_STEP,
  OFFER_CAMPAIGN_BY_GUID,
  OFFER_OPERATION_BY_GUID,
  OFFER_REGISTER_SIGNATURE
} from 'store/actions/offer';
import { lsGetOfferGuid } from 'helpers/store';
import { OFFER_COOKIE_TO_PAGE } from 'store/enums';

const Layout = () => import(/* webpackChunkName: "pages/main" */ 'layout');
const Error = () => import(/* webpackChunkName: "pages/views/error" */ 'views/Error');
const Legal = () => import(/* webpackChunkName: "pages/views/legal" */ 'views/Legal');

const ContractStep1Identity = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-1-identity" */ 'views/Contract/Step-1/Identity'
  );
const ContractStep2UploadDocApproved = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-2-approved" */ 'views/Contract/Step-2/UploadDocApproved'
  );
const ContractStep2UploadDocStudy = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-2-study" */ 'views/Contract/Step-2/UploadDocStudy'
  );
const ContractStep3UploadDocSummary = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-2-uploaddoc" */ 'views/Contract/Step-3/UploadDocSummary'
  );
const ContractStep4Summary = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-4-summary" */ 'views/Contract/Step-4/Summary'
  );
const ContractStep5Thanks = () =>
  import(
    /* webpackChunkName: "pages/views/contract-step-5-thanks" */ 'views/Contract/Step-5/Thanks'
  );
// Redirect to the error page
const redirectToErrorPage = (next, resource) =>
  next({
    name: 'error',
    params: { resource }
  });

// Redirect to the 404 page
const redirectTo404Page = (next, path) => next({ name: '404', params: { 0: path } });

// Redirect to name router
const redirectToRuterName = (data, next, toRouterName = true) => {
  if (toRouterName && data && data.routerName !== 'redirect') {
    const routerName = data.routerName;
    next({ name: routerName });
  } else if (toRouterName && data && data.url) {
    window.location.href = data.url;
  } else {
    next();
  }
};

export default [
  {
    path: '/:guid([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})',
    component: Layout,
    children: [
      {
        path: '/',
        component: ContractStep1Identity,
        meta: { titleKey: 'step1Identity' },
        name: 'step-1-identity',
        beforeEnter(to, from, next) {
          store
            .dispatch(`offer/${OFFER_INIT_GUID}`, to.params)
            .then(resp => {
              if (!resp || (resp && resp === 'identify')) {
                redirectToRuterName(resp, next);
              } else {
                redirectToRuterName(resp, next);
              }
            })
            .catch(() => {
              redirectToErrorPage(next, OFFER_INIT_GUID);
            });
        }
      }
    ]
  },
  {
    path: '/contract',
    component: Layout,
    children: [
      {
        path: '/',
        beforeEnter(to, from, next) {
          redirectTo404Page(next, to.path);
        }
      },
      {
        path: 'step-2-uploaddoc-approved',
        component: ContractStep2UploadDocApproved,
        name: 'step-2-uploaddoc-approved',
        meta: { titleKey: 'step2Approved', step: 2 },
        props: { stepName: 'step-2-uploaddoc-approved' },
        beforeEnter(to, from, next) {
          const cookieToPage = OFFER_COOKIE_TO_PAGE['5a'];
          Promise.all([
            store.commit(`offer/${OFFER_SET_CURRENT_STEP}`, cookieToPage.step),
            store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, 'step-2-uploaddoc-approved'),
            store.dispatch(`offer/${OFFER_DOCUMENTS_REQUIRED_BY_GUID}`)
          ])
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
            });
        }
      },
      {
        path: 'step-2-uploaddoc-study',
        component: ContractStep2UploadDocStudy,
        name: 'step-2-uploaddoc-study',
        meta: { titleKey: 'step2Study', step: 2 },
        props: { stepName: 'step-2-uploaddoc-study' },
        beforeEnter(to, from, next) {
          const cookieToPage = OFFER_COOKIE_TO_PAGE['5c'];
          Promise.all([
            store.commit(`offer/${OFFER_SET_CURRENT_STEP}`, cookieToPage.step),
            store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, 'step-2-uploaddoc-study'),
            store.dispatch(`offer/${OFFER_DOCUMENTS_REQUIRED_BY_GUID}`)
          ])
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
            });
        }
      },
      {
        path: 'step-3-uploaddoc-summary',
        component: ContractStep3UploadDocSummary,
        name: 'step-3-uploaddoc-summary',
        meta: { titleKey: 'step3', step: 3 },
        props: { stepName: 'paso-3-uploaddoc-summary' },
        beforeEnter(to, from, next) {
          const cookieToPage = OFFER_COOKIE_TO_PAGE['5d'];
          Promise.all([
            store.commit(`offer/${OFFER_SET_CURRENT_STEP}`, cookieToPage.step),
            store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, 'step-3-uploaddoc-summary')
          ])
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
            });
        }
      },
      {
        path: 'step-4-summary',
        component: ContractStep4Summary,
        name: 'step-4-summary',
        meta: { titleKey: 'step4', step: 4 },
        props: { stepName: 'step-4-summary' },
        beforeEnter(to, from, next) {
          const cookieToPage = OFFER_COOKIE_TO_PAGE['6'];
          Promise.all([
            store.dispatch(`offer/${OFFER_LEGAL_CONDITIONS}`, '6'),
            store.commit(`offer/${OFFER_SET_CURRENT_STEP}`, cookieToPage.step),
            store.dispatch(`offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`, 'step-4-summary')
          ])
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
            });
        }
      },
      {
        path:
          '/completed/:guid([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/ok',
        component: ContractStep5Thanks,
        name: 'step-5-thanks',
        meta: { titleKey: 'step5' },
        beforeEnter(to, from, next) {
          const status = to.query.status;
          const paramsToRegister = to.query;
          paramsToRegister.gvGuid = to.params.guid;
          const cookieToPage =
            OFFER_COOKIE_TO_PAGE[status === 'signed' ? 'step-5-thanks' : 'no-signed'];
          Promise.all([
            store.commit(`offer/${OFFER_SET_CURRENT_STEP}`, cookieToPage.step),
            store.dispatch(
              `offer/${OFFER_COMPONENTS_BY_PARTNER_ID}`,
              status === 'signed' ? 'step-5-thanks' : 'no-signed'
            ),
            store.dispatch(`offer/${OFFER_CAMPAIGN_BY_GUID}`, to.params.guid || lsGetOfferGuid()),
            store.dispatch(
              `offer/${OFFER_OPERATION_BY_GUID}`,
              to.name,
              to.params.guid || lsGetOfferGuid()
            ),
            store.dispatch(`offer/${OFFER_REGISTER_SIGNATURE}`, paramsToRegister)
          ])
            .then(() => {
              next();
            })
            .catch(() => {
              redirectToErrorPage(next, OFFER_COMPONENTS_BY_PARTNER_ID);
            });
        }
      }
    ]
  },
  {
    path: '/',
    component: Layout,
    props: { whithoutSubHeader: true },
    children: [
      {
        path: 'legal/:type',
        component: Legal,
        name: 'legal',
        props: { whithoutLinksFooter: true },
        meta: { titleKey: 'legal' }
      },
      {
        path: 'error',
        name: 'error',
        component: Error,
        props: { type: '500_es-ES' },
        meta: { titleKey: 'error' }
      },
      {
        path: '*',
        name: '404',
        component: Error,
        props: { type: '404_es-ES' },
        meta: { titleKey: 'notFound' }
      }
    ]
  }
];
